import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BackendService, HttpOptions } from '@ipg/ngx-frontend-toolset';

/**
 * Period information for statistics
 */
export interface PeriodInfo {
  startDate: string;
  endDate: string;
}

/**
 * Numeric card statistics
 */
export interface NumericCards {
  planned: number;
  completed: number;
  notCompleted: number;
  missed: number;
}

/**
 * Time range for a timeblock
 */
export interface TimeRange {
  start: string; // "06:00"
  end: string; // "12:00"
}

/**
 * Timeblock configuration
 */
export interface TimeblockConfig {
  morning: TimeRange;
  afternoon: TimeRange;
  evening: TimeRange;
}

/**
 * Data counts per timeblock
 */
export interface TimeblockData {
  morning: number;
  afternoon: number;
  evening: number;
  total: number;
}

/**
 * Statistics for a single time bucket (day, week, or month)
 */
export interface BucketStatistics {
  date: string; // "2025-02-03" | "2025-W06" | "2025-02"
  planned: number;
  completed: number;
  notCompleted: number;
  missed: number;
  completedByTimeblock: TimeblockData;
  robotOnlineByTimeblock: TimeblockData;
}

/**
 * Completion summary counts
 */
export interface CompletionSummary {
  completed: number;
  notCompleted: number;
  missed: number;
}

/**
 * Bucketed SCM block click counts grouped by block type
 */
export interface ScmBlockBucketData {
  date: string; // "2025-02-03" | "2025-W06" | "2025-02"
  total: number;
  audio: number;
  image: number;
  interaction: number;
  folder: number;
}

/**
 * Aggregated SCM block click statistics for a period
 */
export interface ScmBlockStatistics {
  bucketData: ScmBlockBucketData[];
  totalClicks: number;
}

/**
 * Complete client statistics response
 */
export interface ClientStatisticsResponse {
  period: PeriodInfo;
  bucketSize: string;
  numericCards: NumericCards;
  bucketData: BucketStatistics[];
  completionSummary: CompletionSummary;
  timeblockConfig: TimeblockConfig;
  scmBlockStatistics: ScmBlockStatistics;
}

/**
 * A window in which an SCM block is known to have been active (clickable),
 * clipped to the requested period. `end` is null while the block is still active.
 */
export interface ScmPresenceInterval {
  start: string;
  end: string | null;
}

/**
 * Hour-of-day click counts and presence history for a single SCM block.
 *
 * `hours` and `presentDaysPerHour` are parallel 24-element arrays indexed by
 * hour of day. Together they carry the three cell states of the heatmap:
 * clicked, a dead hour (present but never clicked), and not comparable
 * (absent or unknown) — which must never be rendered as a number.
 */
export interface ScmClickBlockStatistics {
  scmId: string;
  type: 'audio' | 'image' | 'interaction' | 'folder';
  label: string;
  avatarFileId: string | null;
  deleted: boolean;
  hours: number[];
  presentDaysPerHour: number[];
  presence: ScmPresenceInterval[];
  presenceKnownFrom: string | null;
  totalClicks: number;
  activeDays: number;
}

/**
 * Hour-of-day SCM click statistics for one client over a period
 */
export interface ScmClickStatisticsResponse {
  period: PeriodInfo;
  timezone: string;
  blocks: ScmClickBlockStatistics[];
  totalClicks: number;
}

/**
 * Request parameters for client statistics
 */
export interface ClientStatisticsParams {
  clientId: string;
  startDate: Date;
  endDate: Date;
  timezone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ClientStatisticsService {
  private readonly _backend = inject(BackendService);

  /**
   * Get statistics for a client in a specified period
   *
   * @param params - Request parameters including clientId, date range, and timezone
   * @param options - Optional HTTP options
   * @returns Observable of ClientStatisticsResponse
   */
  getStatistics(
    params: ClientStatisticsParams,
    options?: HttpOptions
  ): Observable<ClientStatisticsResponse> {
    const queryParams: Record<string, string> = {
      start_date: params.startDate.toISOString(),
      end_date: params.endDate.toISOString(),
    };

    if (params.timezone) {
      queryParams['timezone'] = params.timezone;
    }

    return this._backend.get(`/clients/${params.clientId}/statistics`, {
      ...options,
      params: queryParams,
    });
  }

  /**
   * Get per-block, hour-of-day SCM click statistics for a client
   *
   * Separate from getStatistics() because it returns per-block rows rather
   * than period buckets.
   *
   * @param params - Request parameters including clientId, date range, and timezone
   * @param options - Optional HTTP options
   * @returns Observable of ScmClickStatisticsResponse
   */
  getScmClickStatistics(
    params: ClientStatisticsParams,
    options?: HttpOptions
  ): Observable<ScmClickStatisticsResponse> {
    const queryParams: Record<string, string> = {
      start_date: params.startDate.toISOString(),
      end_date: params.endDate.toISOString(),
    };

    if (params.timezone) {
      queryParams['timezone'] = params.timezone;
    }

    return this._backend.get(`/clients/${params.clientId}/statistics/scm-clicks`, {
      ...options,
      params: queryParams,
    });
  }
}
